import * as React from "react";
import { FolderOpen, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { type CollectedUploadFile } from "../../upload/types";

interface Props {
  files: readonly CollectedUploadFile[];
  disabled: boolean;
  dropzoneProps: React.HTMLAttributes<HTMLDivElement>;
  inputProps: React.InputHTMLAttributes<HTMLInputElement>;
  onChooseFiles: () => void;
  onFolderFiles: (files: readonly File[]) => void;
  onChange: () => void;
}

const formatFileSize = (bytes: number): string => {
  if (bytes < 1_000) return `${bytes} bytes`;
  if (bytes < 1_000_000) return `${Number((bytes / 1_000).toFixed(1))} KB`;
  if (bytes < 1_000_000_000) return `${Number((bytes / 1_000_000).toFixed(1))} MB`;
  return `${Number((bytes / 1_000_000_000).toFixed(1))} GB`;
};

const directorySummary = (files: readonly CollectedUploadFile[]) => {
  const roots = new Set(files.map(file => file.relativePath.split("/")[0]).filter(Boolean));
  const root = roots.size === 1 ? [...roots][0] : "Selected folders";
  const folders = new Set<string>();
  for (const selectedFile of files) {
    const segments = selectedFile.directoryPath.split("/").filter(Boolean);
    for (let index = 1; index < segments.length; index += 1) {
      folders.add(segments.slice(0, index + 1).join("/"));
    }
  }
  return {
    root,
    subfolderCount: folders.size,
    totalBytes: files.reduce((total, selectedFile) => total + selectedFile.file.size, 0),
  };
};

export const UploadSelection: React.FC<Props> = ({
  files,
  disabled,
  dropzoneProps,
  inputProps,
  onChooseFiles,
  onFolderFiles,
  onChange,
}) => {
  const folderInputRef = React.useRef<HTMLInputElement>(null);
  const hasDirectoryPaths = files.some(file => file.directoryPath.length > 0);

  React.useEffect(() => {
    folderInputRef.current?.setAttribute("webkitdirectory", "");
  }, []);

  const summary = hasDirectoryPaths ? directorySummary(files) : undefined;

  return (
    <div className="space-y-3">
      <div
        {...dropzoneProps}
        className={`flex min-h-28 flex-col items-center justify-center rounded-lg border border-dashed border-midnight-blue-200 bg-slate-50/40 px-5 py-4 text-center transition-colors ${
          disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer hover:border-coral hover:bg-ivory-mist-100"
        }`}
      >
        <input {...inputProps} />
        <input
          ref={folderInputRef}
          type="file"
          multiple
          className="sr-only"
          aria-label="Choose folder"
          onClick={event => event.stopPropagation()}
          onChange={event => {
            onFolderFiles(Array.from(event.currentTarget.files ?? []));
            event.currentTarget.value = "";
          }}
        />
        <div className="mb-2 flex h-8 w-8 items-center justify-center rounded-md bg-ivory-mist-300">
          <Upload aria-hidden="true" className="h-4 w-4 text-midnight-blue-500" />
        </div>
        <p className="text-sm font-semibold text-midnight-blue-500">Drop files or folders here</p>
        <div className="mt-0.5 flex items-center justify-center gap-1 text-xs text-muted-foreground">
          <span>or</span>
          <Button
            type="button"
            variant="link"
            size="xs"
            className="h-auto px-1 py-0 text-coral"
            disabled={disabled}
            onClick={event => {
              event.stopPropagation();
              onChooseFiles();
            }}
          >
            choose files
          </Button>
          <span>or</span>
          <Button
            type="button"
            variant="link"
            size="xs"
            className="h-auto px-1 py-0 text-coral"
            disabled={disabled}
            onClick={event => {
              event.stopPropagation();
              folderInputRef.current?.click();
            }}
          >
            choose a folder
          </Button>
        </div>
        <p className="mt-2 text-xs text-muted-foreground">
          PDF, Word, Excel, PowerPoint, images, email, and ZIP files are accepted
        </p>
      </div>
      {summary && (
        <div className="flex items-center gap-3 rounded-lg border border-desert-sand-200 bg-white p-2.5">
          <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-ivory-mist-300">
            <FolderOpen aria-hidden="true" className="h-4 w-4 text-midnight-blue-400" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-midnight-blue-500">{summary.root}</p>
            <p className="mt-0.5 text-xs text-muted-foreground">
              {files.length} {files.length === 1 ? "file" : "files"} · {summary.subfolderCount}{" "}
              {summary.subfolderCount === 1 ? "subfolder" : "subfolders"} · {formatFileSize(summary.totalBytes)}
            </p>
          </div>
          <Button type="button" variant="ghost" size="xs" disabled={disabled} onClick={onChange}>
            Change
          </Button>
        </div>
      )}
    </div>
  );
};
